/**
 * Payment success / failure result pages — both rendered by this one
 * component, switched by the `outcome` prop ("success" | "failure").
 *
 * Razorpay redirects post-payment to /payment/success?payment_id=…
 * (or /payment/failure?reason=…). The user gets a clear status, the
 * transaction reference, and a CTA back into the product.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight, RotateCw } from "lucide-react";
import PublicLayout from "./PublicLayout";

export default function PaymentResultPage({ outcome = "success" }) {
    const [params] = useSearchParams();
    const paymentId = params.get("payment_id") || params.get("razorpay_payment_id");
    const orderId = params.get("order_id") || params.get("razorpay_order_id");
    const reason = params.get("reason") || params.get("error") || "Payment was not completed.";
    const planId = params.get("plan");
    const [signedIn, setSignedIn] = useState(false);

    useEffect(() => {
        // Cheap probe: do we have an auth cookie? `/auth/me` will say so.
        const url = process.env.REACT_APP_BACKEND_URL + "/api/auth/me";
        fetch(url, { credentials: "include" }).then(r => setSignedIn(r.ok)).catch(() => setSignedIn(false));
    }, []);

    const isSuccess = outcome === "success";

    return (
        <PublicLayout>
            <div className="max-w-xl mx-auto text-center py-10" data-testid={`payment-${outcome}`}>
                {isSuccess ? (
                    <>
                        <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" strokeWidth={1.25}/>
                        <h1 className="text-3xl sm:text-4xl font-semibold text-foreground tracking-tight" data-testid="payment-success-title">Payment successful</h1>
                        <p className="text-muted-foreground mt-3 text-sm">
                            Your access has been provisioned instantly. A receipt has been emailed to your registered address.
                        </p>
                    </>
                ) : (
                    <>
                        <XCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" strokeWidth={1.25}/>
                        <h1 className="text-3xl sm:text-4xl font-semibold text-foreground tracking-tight" data-testid="payment-failure-title">Payment could not be completed</h1>
                        <p className="text-muted-foreground mt-3 text-sm" data-testid="payment-failure-reason">{reason}</p>
                        <p className="text-[10px] text-muted-foreground mt-2">No amount has been debited. If your account was charged in error, please email <a href="mailto:support@lawos.in" className="text-primary">support@lawos.in</a> with the transaction reference.</p>
                    </>
                )}

                {(paymentId || orderId) && (
                    <div className="mt-6 border border-border bg-card p-4 text-left inline-block min-w-[280px]">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Transaction reference</div>
                        {paymentId && <div className="text-xs"><span className="text-muted-foreground">Payment ID:</span> <span className="text-foreground font-mono" data-testid="payment-id">{paymentId}</span></div>}
                        {orderId && <div className="text-xs mt-1"><span className="text-muted-foreground">Order ID:</span> <span className="text-foreground font-mono" data-testid="order-id">{orderId}</span></div>}
                    </div>
                )}

                <div className="mt-8 flex items-center justify-center gap-3">
                    {isSuccess ? (
                        <Link to={signedIn ? "/app" : "/login"} data-testid="payment-cta-dashboard"
                            className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary px-5 py-2.5 text-xs uppercase tracking-[0.16em] font-medium">
                            Open dashboard <ArrowRight className="w-3.5 h-3.5"/>
                        </Link>
                    ) : (
                        <>
                            <Link to={planId ? `/checkout?plan=${planId}` : "/pricing"} data-testid="payment-cta-retry"
                                className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary px-5 py-2.5 text-xs uppercase tracking-[0.16em] font-medium">
                                <RotateCw className="w-3.5 h-3.5"/> Try again
                            </Link>
                            <Link to="/legal/contact" data-testid="payment-cta-contact"
                                className="inline-flex items-center gap-2 border border-border text-muted-foreground hover:text-foreground px-5 py-2.5 text-xs uppercase tracking-[0.16em]">
                                Contact support
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </PublicLayout>
    );
}
